import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Employee Details Service
export const employeeDetailsService = {
  // Get detailed employee information
  async getEmployeeDetails(employeeId) {
    try {
      const response = await api.get(`/employees/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee details:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee details');
    }
  },

  // Get employee documents
  async getEmployeeDocuments(employeeId, params = {}) {
    try {
      const response = await api.get(`/documents`, {
        params: {
          employeeId,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee documents:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee documents');
    }
  },

  // Get employee performance reviews
  async getEmployeePerformance(employeeId, params = {}) {
    try {
      const response = await api.get(`/performance/reviews`, {
        params: {
          employeeId,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee performance:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee performance');
    }
  },

  // Get employee attendance
  async getEmployeeAttendance(employeeId, params = {}) {
    try {
      const response = await api.get(`/attendance`, {
        params: {
          employeeId,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee attendance:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee attendance');
    }
  },

  // Get employee contract information
  async getEmployeeContract(employeeId) {
    try {
      const response = await api.get(`/documents`, {
        params: {
          employeeId,
          category: 'contract',
          type: 'contract'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee contract:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee contract');
    }
  },

  // Upload employee document
  async uploadEmployeeDocument(employeeId, formData) {
    try {
      const response = await api.post(`/documents`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          employeeId
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading employee document:', error);
      throw new Error(error.response?.data?.error || 'Failed to upload employee document');
    }
  },

  // Download employee document
  async downloadEmployeeDocument(documentId) {
    try {
      const response = await api.get(`/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading employee document:', error);
      throw new Error(error.response?.data?.error || 'Failed to download employee document');
    }
  },

  // Get employee statistics
  async getEmployeeStats(employeeId) {
    try {
      const response = await api.get(`/employees/stats`, {
        params: { employeeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee stats:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee stats');
    }
  },

  // Get employee history
  async getEmployeeHistory(employeeId, params = {}) {
    try {
      const response = await api.get(`/employees/${employeeId}/history`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee history:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee history');
    }
  },

  // Update employee information
  async updateEmployee(employeeId, employeeData) {
    try {
      const response = await api.put(`/employees/${employeeId}`, employeeData);
      return response.data;
    } catch (error) {
      console.error('Error updating employee:', error);
      throw new Error(error.response?.data?.error || 'Failed to update employee');
    }
  },

  // Get employee emergency contacts
  async getEmployeeEmergencyContacts(employeeId) {
    try {
      const response = await api.get(`/employees/${employeeId}/emergency-contacts`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee emergency contacts:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee emergency contacts');
    }
  },

  // Get employee leave balance
  async getEmployeeLeaveBalance(employeeId) {
    try {
      const response = await api.get(`/leave/balance`, {
        params: { employeeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee leave balance:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee leave balance');
    }
  },

  // Get employee salary information
  async getEmployeeSalary(employeeId) {
    try {
      const response = await api.get(`/payroll/employee/${employeeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee salary:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch employee salary');
    }
  }
}; 